import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, m } from "framer-motion";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../cn";
import { springSoft, useMotionTransition } from "../motion";

export interface DialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  /**
   * Sub-heading under the title. A node rather than a string so a dialog about
   * a concrete thing can identify it the way the rest of the app does -- a
   * `LicensePlate` rather than its plate text, for instance. Radix renders it
   * in a `<p>`, so keep the content phrasing-level.
   */
  readonly description?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
  /**
   * Classes for the body wrapper around `children`.
   *
   * A dialog that fills the viewport and scrolls internally needs its body to
   * be a flex child of the panel (`min-h-0 flex-1`) and usually to drop the
   * default padding, which the wrapper otherwise imposes. Without this the
   * wrapper sizes to its content and any `flex-1`/`overflow` on the child has
   * no constrained parent to resolve against, so the content is simply clipped.
   */
  readonly bodyClassName?: string;
}

/**
 * Controlled, accessible modal (Radix Dialog) with framer enter/exit. Radix
 * handles focus trapping, escape-to-close and scroll locking; framer adds the
 * motion via `forceMount` + AnimatePresence.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  bodyClassName,
}: DialogProps) {
  const overlayTransition = useMotionTransition({ duration: 0.2 });
  const contentTransition = useMotionTransition(springSoft);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <m.div
                className="fixed inset-0 z-50 bg-foreground/35 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={overlayTransition}
              />
            </DialogPrimitive.Overlay>
            <div className="fixed inset-0 z-50 grid place-items-center p-4">
              <DialogPrimitive.Content
                asChild
                forceMount
                // Radix links the panel to its Description through an id it
                // generates upfront, and warns when that id resolves to
                // nothing. A dialog without a description is deliberate (the
                // title, or the body, carries the whole meaning), so the link
                // is dropped for that case only -- spreading it unconditionally
                // would also unlink the descriptions that do exist.
                {...(description ? {} : { "aria-describedby": undefined })}
              >
                <m.div
                  initial={{ opacity: 0, scale: 0.96, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97, y: 8 }}
                  transition={contentTransition}
                  className={cn(
                    "relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-lg focus:outline-none",
                    className,
                  )}
                >
                  <div className="flex items-start justify-between gap-4 px-6 pt-5">
                    <div className="space-y-1">
                      <DialogPrimitive.Title className="text-lg font-semibold tracking-tight">
                        {title}
                      </DialogPrimitive.Title>
                      {description && (
                        <DialogPrimitive.Description className="text-sm text-muted-foreground">
                          {description}
                        </DialogPrimitive.Description>
                      )}
                    </div>
                    <DialogPrimitive.Close
                      className="grid size-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="Sluiten"
                    >
                      <X className="size-4" />
                    </DialogPrimitive.Close>
                  </div>
                  {children && (
                    <div className={cn("px-6 py-4", bodyClassName)}>
                      {children}
                    </div>
                  )}
                  {footer && (
                    <div className="flex items-center justify-end gap-3 border-t border-border/60 bg-surface-muted/50 px-6 py-4">
                      {footer}
                    </div>
                  )}
                </m.div>
              </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
